import React, { useEffect, useState } from "react";
import { getDigest, getDigestConfig, sendTestDigest } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Search, Send, Bookmark, TrendingUp, Loader2, Calendar, Mail } from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-6">
      <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center"><Icon size={18} className="text-brand-600" /></div>
      <div className="mt-4 font-display text-[30px] font-semibold text-foreground">{value}</div>
      <div className="text-[13px] text-muted-foreground">{label}</div>
    </div>
  );
}

export default function Digest() {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    getDigest().then(setD).catch(() => {});
    getDigestConfig().then(setCfg).catch(() => {});
  }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      await sendTestDigest();
      toast({ title: "Digest sent", description: `Check ${cfg?.to || "your inbox"}.` });
    } catch (e) {
      toast({ title: "Send failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

  return (
    <main className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-display text-[26px] font-bold text-foreground">Weekly digest</h1>
        {cfg?.email_configured && (
          <button onClick={sendTest} disabled={sending}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium bg-brand-600 text-white rounded-lg px-3.5 py-2 hover:bg-brand-700 disabled:opacity-50 transition-colors">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send test digest now
          </button>
        )}
      </div>
      {d && (
        <p className="text-[14px] text-muted-foreground mb-2 inline-flex items-center gap-1.5">
          <Calendar size={14} /> {fmt(d.range_start)} – {fmt(d.range_end)}
        </p>
      )}
      {cfg?.email_configured && (
        <div className="mb-6 inline-flex items-center gap-2 text-[13px] text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">
          <Mail size={14} /> Emailed automatically every <b>Monday {cfg.hour_pacific}:00 Pacific</b> to <b>{cfg.to}</b>
        </div>
      )}

      {!d ? (
        <div className="py-16 text-center text-muted-foreground"><Loader2 size={22} className="mx-auto animate-spin" /></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Stat icon={Search} label="Leads found" value={d.leads_found.toLocaleString()} />
            <Stat icon={TrendingUp} label="Searches run" value={d.searches_run} />
            <Stat icon={Send} label="Leads pushed to Instantly" value={d.leads_pushed.toLocaleString()} />
            <Stat icon={Bookmark} label="Lists saved" value={d.lists_saved} />
          </div>

          <div className="mt-6 bg-white rounded-2xl border border-border p-6">
            <h3 className="font-display text-[17px] font-semibold text-foreground mb-4">Top searches this week</h3>
            {d.top_searches.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">No searches yet this week.</p>
            ) : (
              <div className="space-y-3">
                {d.top_searches.map((s, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className="text-foreground font-medium">{s.label}</span>
                      <span className="text-muted-foreground">{s.leads} leads</span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-brand-500 rounded-full" style={{ width: `${Math.min(100, (s.leads / (d.top_searches[0].leads || 1)) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="mt-4 text-[12px] text-muted-foreground">This same summary is emailed to you automatically every Monday.</p>
        </>
      )}
    </main>
  );
}
